/// <reference no-default-lib="true" />
/// <reference lib="esnext" />
/// <reference lib="dom" />

/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   File:   tabs-automatic.ts
 *
 *   Desc:   Tablist widget that implements ARIA Authoring Practices
 */

class Tabs {
  private tablistNode: HTMLElement
  private tabs: HTMLButtonElement[] = []
  private tabpanels: HTMLElement[] = []
  private firstTab: HTMLButtonElement | null = null
  private lastTab: HTMLButtonElement | null = null

  constructor(groupNode: HTMLElement) {
    this.tablistNode = groupNode

    this.tabs = Array.from(
      this.tablistNode.querySelectorAll<HTMLButtonElement>('.tabs__button'),
    )

    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i]

      const controlsId = tab.getAttribute('aria-controls')
      const tabpanel = controlsId ? document.getElementById(controlsId) : null

      if (!tabpanel) {
        // If markup is inconsistent, skip wiring this tab to avoid runtime errors.
        // (W3C example assumes this exists.)
        continue
      }

      tab.tabIndex = -1
      tab.setAttribute('aria-selected', 'false')
      this.tabpanels.push(tabpanel)

      tab.addEventListener('keydown', this.onKeydown)
      tab.addEventListener('click', this.onClick)

      if (!this.firstTab) this.firstTab = tab
      this.lastTab = tab
    }

    if (this.firstTab) {
      this.setSelectedTab(this.firstTab, false)
    }
  }

  private setSelectedTab = (
    currentTab: HTMLButtonElement,
    setFocus: boolean = true,
  ) => {
    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i]
      const panel = this.tabpanels[i]

      // If we skipped a tab due to missing panel, keep indices aligned as best we can.
      // (In a stricter implementation, you'd validate markup up front.)
      if (!panel) continue

      if (currentTab === tab) {
        tab.setAttribute('aria-selected', 'true')
        tab.removeAttribute('tabindex')
        panel.classList.remove('is-hidden')
        if (setFocus) tab.focus()
      } else {
        tab.setAttribute('aria-selected', 'false')
        tab.tabIndex = -1
        panel.classList.add('is-hidden')
      }
    }
  }

  private setSelectedToPreviousTab(currentTab: HTMLButtonElement) {
    if (!this.firstTab || !this.lastTab) return

    if (currentTab === this.firstTab) {
      this.setSelectedTab(this.lastTab)
      return
    }

    const index = this.tabs.indexOf(currentTab)
    if (index > 0) this.setSelectedTab(this.tabs[index - 1])
  }

  private setSelectedToNextTab(currentTab: HTMLButtonElement) {
    if (!this.firstTab || !this.lastTab) return

    if (currentTab === this.lastTab) {
      this.setSelectedTab(this.firstTab)
      return
    }

    const index = this.tabs.indexOf(currentTab)
    if (index >= 0 && index < this.tabs.length - 1) {
      this.setSelectedTab(this.tabs[index + 1])
    }
  }

  /* EVENT HANDLERS */

  private onKeydown = (event: KeyboardEvent) => {
    const tgt = event.currentTarget as HTMLButtonElement | null
    if (!tgt) return

    let flag = false

    switch (event.key) {
      case 'ArrowLeft':
        this.setSelectedToPreviousTab(tgt)
        flag = true
        break

      case 'ArrowRight':
        this.setSelectedToNextTab(tgt)
        flag = true
        break

      case 'Home':
        if (this.firstTab) this.setSelectedTab(this.firstTab)
        flag = true
        break

      case 'End':
        if (this.lastTab) this.setSelectedTab(this.lastTab)
        flag = true
        break

      default:
        break
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  private onClick = (event: MouseEvent) => {
    const tgt = event.currentTarget as HTMLButtonElement | null
    if (!tgt) return
    this.setSelectedTab(tgt)
  }
}

// Initialize tablist

window.addEventListener('load', () => {
  const tablists = document.querySelectorAll<HTMLElement>('[role="tablist"]')
  for (let i = 0; i < tablists.length; i += 1) {
    if (tablists[i].dataset.hydrated) continue
    tablists[i].dataset.hydrated = 'true'
    new Tabs(tablists[i])
  }

  setupGlobalTriggers()
})

function setupGlobalTriggers(): void {
  const tabTriggers = Array.from(
    document.querySelectorAll('[data-trigger-tab]'),
  )

  for (const tabTrigger of tabTriggers) {
    if (tabTrigger instanceof HTMLElement && tabTrigger.dataset.hydrated) {
      continue
    }
    if (tabTrigger instanceof HTMLElement) tabTrigger.dataset.hydrated = 'true'
    const tabId = tabTrigger.getAttribute('data-trigger-tab')
    const groupId = tabTrigger.getAttribute('data-trigger-tab-group')

    if (!tabId || !groupId) {
      continue
    }

    const selector =
      `[data-tab-group="${groupId}"] [aria-controls="tabpanel-${tabId}"]`
    const tabButton = document.querySelector(selector) as
      | HTMLButtonElement
      | null

    if (tabButton) {
      tabTrigger.addEventListener('click', () => {
        tabButton?.click?.()
      })
    }
  }
}
